import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import { Chart, CommonSeriesSettings, Series, Legend, Tooltip, ValueAxis, Label, ArgumentAxis } from "devextreme-react/chart";
import { controlTypes, externalApiTypes } from "../../../../Config/constants";

export default function InsightsTab({ allMeasurements = [], graphProjects = [], currency, loading, timeRangeDates }) {
  const classes = useStyles();
  const currencySymbol = currency?.symbol || "€";

  // ─── Derived data ───────────────────────────────────────────────

  const insights = useMemo(() => {
    if (!allMeasurements || allMeasurements.length === 0) {
      return null;
    }

    const measurements = allMeasurements;

    // Total value created (profit items)
    const totalValueCreated = measurements.filter((m) => m.isProfit && m.eventPrice > 0).reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Total production loss (non-profit / loss items)
    const totalProductionLoss = measurements.filter((m) => !m.isProfit && m.eventPrice > 0).reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Netto contribution
    const nettoContribution = totalValueCreated - totalProductionLoss;

    // Date range for per-day calculations (use daterange filter)
    const rangeStart = timeRangeDates?.fromDate ? new Date(timeRangeDates.fromDate) : null;
    const rangeEnd = timeRangeDates?.endDate ? new Date(timeRangeDates.endDate) : null;
    const daySpan = rangeStart && rangeEnd ? Math.max(1, Math.ceil((rangeEnd - rangeStart) / 86400000)) : 1;

    // ─── Loss by line (per registration point / project resource) ───
    const lossByLine = {};
    measurements
      .filter((m) => !m.isProfit && m.eventPrice > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].value += m.eventPrice || 0;
      });
    // Add duration from all non-profit stopwatch measurements (regardless of eventPrice)
    measurements
      .filter((m) => !m.isProfit && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].duration += m.durationInMinutes;
      });

    const lossByLineData = Object.entries(lossByLine)
      .map(([name, data]) => ({
        name,
        value: data.value,
        duration: data.duration,
        lossPerWeek: daySpan > 0 ? (data.value / daySpan) * 7 : 0,
        yearlyImpact: daySpan > 0 ? (data.value / daySpan) * 365 : 0,
      }))
      .sort((a, b) => b.value - a.value);

    // ─── Top loss drivers (top 5 descriptions / reasons) ───
    const lossByReason = {};
    measurements
      .filter((m) => !m.isProfit && m.eventPrice > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0 };
        lossByReason[reason].value += m.eventPrice || 0;
      });
    // Add duration from all non-profit stopwatch measurements (regardless of eventPrice)
    measurements
      .filter((m) => !m.isProfit && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0 };
        lossByReason[reason].duration += m.durationInMinutes;
      });

    const topLossDrivers = Object.entries(lossByReason)
      .map(([reason, data]) => ({
        reason,
        value: data.value,
        duration: data.duration,
        percentage: totalProductionLoss > 0 ? ((data.value / totalProductionLoss) * 100).toFixed(1) : 0,
      }))
      .sort((a, b) => a.value - b.value)
      .slice(-5);

    // ─── Stopwatch-based stats (disruptions) ───
    const stopwatchMeasurements = measurements.filter(
      (m) => m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH
    );
    const stopwatchLossMeasurements = stopwatchMeasurements.filter((m) => !m.isProfit);

    // Average loss per day
    const avgLossPerDay = totalProductionLoss / daySpan;

    // Average disruptions per day
    const avgDisruptionsPerDay = stopwatchLossMeasurements.length / daySpan;

    // Average disruption duration (minutes)
    const durationsMinutes = stopwatchLossMeasurements
      .map((m) => m.durationInMinutes)
      .filter((d) => d !== null && d !== undefined && d > 0);
    const avgDisruptionDuration =
      durationsMinutes.length > 0 ? durationsMinutes.reduce((s, d) => s + d, 0) / durationsMinutes.length : 0;

    // Percentage of potential value lost
    const totalPotentialValue = totalValueCreated + totalProductionLoss;
    const lossPercentage = totalPotentialValue > 0 ? ((totalProductionLoss / totalPotentialValue) * 100).toFixed(1) : 0;

    // Weekly and yearly total loss
    const weeklyTotalLoss = daySpan > 0 ? (totalProductionLoss / daySpan) * 7 : 0;
    const yearlyTotalLoss = daySpan > 0 ? (totalProductionLoss / daySpan) * 365 : 0;

    // Loss lines vs total lines
    const lossLineCount = Object.keys(lossByLine).length;
    const totalLineCount = graphProjects.length;

    // Worst performing line
    const worstLine = lossByLineData.length > 0 ? lossByLineData[0] : null;

    return {
      totalValueCreated,
      totalProductionLoss,
      nettoContribution,
      lossPercentage,
      lossByLineData,
      topLossDrivers,
      avgLossPerDay,
      avgDisruptionsPerDay,
      avgDisruptionDuration,
      daySpan,
      weeklyTotalLoss,
      yearlyTotalLoss,
      lossLineCount,
      totalLineCount,
      worstLine,
    };
  }, [allMeasurements, timeRangeDates, graphProjects]);

  // ─── Helpers ────────────────────────────────────────────────────

  const formatCurrency = (value) => {
    return `${currencySymbol} ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h}u ${m}min`;
  };

  // ─── Empty state ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <div className={classes.spinner} />
      </div>
    );
  }

  if (!insights) {
    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyStateCard}>
          <p className={classes.emptyStateCard__title}>No data</p>
          <p className={classes.emptyStateCard__subtitle}>There are no measurements to generate insights</p>
        </div>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <div className={classes.insightsContainer}>
        {/* KPI Cards */}
        <div className={classes.kpiRow}>
            <div className={classes.kpiCard}>
            <div className={classes.kpiCard__indicator} style={{ backgroundColor: "#28A745" }} />
            <div className={classes.kpiCard__content}>
                <p className={classes.kpiCard__label}>Total value created</p>
                <p className={classes.kpiCard__value}>{formatCurrency(insights.totalValueCreated)}</p>
            </div>
            </div>
            <div className={classes.kpiCard}>
            <div className={classes.kpiCard__indicator} style={{ backgroundColor: "#DC3545" }} />
            <div className={classes.kpiCard__content}>
                <p className={classes.kpiCard__label}>Total production loss</p>
                <p className={classes.kpiCard__value}>{formatCurrency(insights.totalProductionLoss)}</p>
            </div>
            </div>
            <div className={classes.kpiCard}>
            <div className={classes.kpiCard__indicator} style={{ backgroundColor: insights.nettoContribution >= 0 ? "#28A745" : "#DC3545" }} />
            <div className={classes.kpiCard__content}>
                <p className={classes.kpiCard__label}>Netto contribution</p>
                <p className={classes.kpiCard__value}>{formatCurrency(insights.nettoContribution)}</p>
            </div>
            </div>
        </div>
        {/* KPI summary */}
        <div className={classes.kpiSummaryCard}>
            <p className={classes.kpiSummaryCard__text}>
                <span className={classes.kpiSummaryCard__highlight}>{insights.lossPercentage}%</span> of potential value is lost due to inefficiencies
            </p>
        </div>

      {/* Charts row */}
      <div className={classes.chartsRow}>
        {/* Loss by line */}
        <div className={classes.chartCard}>
          <p className={classes.chartCard__title}>Loss by line</p>
          {insights.lossByLineData.length > 0 ? (
            <table className={classes.lossTable}>
              <thead>
                <tr>
                  <th className={classes.lossTable__th}>Line</th>
                  <th className={`${classes.lossTable__th} ${classes.lossTable__thRight}`}>Time lost</th>
                  <th className={`${classes.lossTable__th} ${classes.lossTable__thRight}`}>Loss / week</th>
                  <th className={`${classes.lossTable__th} ${classes.lossTable__thRight}`}>Yearly impact</th>
                </tr>
              </thead>
              <tbody>
                {insights.lossByLineData.map((line, idx) => (
                  <tr key={idx} className={classes.lossTable__row}>
                    <td className={classes.lossTable__td}>{line.name}</td>
                    <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight}`}>{formatDuration(line.duration)}</td>
                    <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight}`}>{formatCurrency(line.lossPerWeek)}</td>
                    <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight}`}>{formatCurrency(line.yearlyImpact)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={classes.noDataText}>No loss data available</p>
          )}
        </div>

        {/* Top loss drivers */}
        <div className={classes.chartCard}>
          <p className={classes.chartCard__title}>Top loss drivers</p>
          {insights.topLossDrivers.length > 0 ? (
            <Chart id="topLossDriversChart" dataSource={insights.topLossDrivers} rotated={true}>
              <CommonSeriesSettings argumentField="reason" type="bar" barWidth={32} />
              <Series valueField="value" name="Loss" color="#C3996B" />
              <ArgumentAxis>
                <Label visible={true} />
              </ArgumentAxis>
              <ValueAxis>
                <Label format={{ type: "fixedPoint", precision: 0 }} />
              </ValueAxis>
              <Legend visible={false} />
              <Tooltip
                enabled={true}
                customizeTooltip={(arg) => {
                  const driver = insights.topLossDrivers.find((d) => d.reason === arg.argument);
                  return {
                    text: `${arg.argument}\n${formatCurrency(arg.value)} (${driver?.percentage || 0}%)\n${formatDuration(driver?.duration || 0)}`,
                  };
                }}
              />
            </Chart>
          ) : (
            <p className={classes.noDataText}>No loss drivers available</p>
          )}
        </div>
      </div>

      {/* Key sentences */}
      <div className={classes.sentencesCard}>
        <p className={classes.sentencesCard__title}>Key insights</p>
        <div className={classes.sentencesList}>
          {insights.topLossDrivers.length > 0 && (
            <p className={classes.sentence}>
              <span className={classes.sentence__highlight}>{insights.topLossDrivers[insights.topLossDrivers.length - 1].reason}</span> veroorzaakt{" "}
              <span className={classes.sentence__highlight}>{insights.topLossDrivers[insights.topLossDrivers.length - 1].percentage}%</span> van het productieverlies.{" "}
              {/* <span className={classes.sentence__highlight}>{formatCurrency(insights.topLossDrivers[insights.topLossDrivers.length - 1].value)}</span> */}
            </p>
          )}
          <p className={classes.sentence}>
            Productieverlies bedraagt gemiddeld <span className={classes.sentence__highlight}>{formatCurrency(insights.avgLossPerDay)}</span> per dag
          </p>
          {insights.topLossDrivers.length > 0 && (
            <p className={classes.sentence}>
              <span className={classes.sentence__highlight}>{insights.topLossDrivers[insights.topLossDrivers.length - 1].percentage}%</span> van het verlies komt uit{" "}
              <span className={classes.sentence__highlight}>{insights.topLossDrivers[insights.topLossDrivers.length - 1].reason}</span>
            </p>
          )}
          <p className={classes.sentence}>
            Het wekelijkse productieverlies bedraagt <span className={classes.sentence__highlight}>{formatCurrency(insights.weeklyTotalLoss)}</span>
          </p>
          <p className={classes.sentence}>
            Op jaarbasis wordt <span className={classes.sentence__highlight}>{formatCurrency(insights.yearlyTotalLoss)}</span> aan productieverlies verwacht
          </p>
          <p className={classes.sentence}>
            Verlies wordt geregistreerd op <span className={classes.sentence__highlight}>{insights.lossLineCount} van {insights.totalLineCount}</span> lijnen
          </p>
          {insights.worstLine && (
            <p className={classes.sentence}>
              <span className={classes.sentence__highlight}>{insights.worstLine.name}</span> is de slechtst presterende lijn met{" "}
              <span className={classes.sentence__highlight}>{formatCurrency(insights.worstLine.lossPerWeek)}</span> verlies per week
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  insightsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "16px 0",
    width: "100%",
  },

  // ─── KPI cards ───
  kpiRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  kpiCard: {
    flex: 1,
    minWidth: "200px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
  },

  kpiCard__indicator: {
    width: "4px",
    flexShrink: 0,
  },

  kpiCard__content: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "20px 24px",
  },

  kpiCard__label: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  kpiCard__value: {
    margin: 0,
    fontSize: "28px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "#0C0E29",
  },

  kpiSummaryCard: {
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: "#FFF9ED",
    border: "1px solid #FEF1D6",
  },

  kpiSummaryCard__text: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    margin: 0,
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  kpiSummaryCard__highlight: {
    fontWeight: 700,
    fontSize: "18px",
  },

  // ─── Charts ───
  chartsRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  chartCard: {
    flex: 1,
    minWidth: "300px",
    padding: "20px 24px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
  },

  chartCard__title: {
    margin: "0 0 12px 0",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#0C0E29",
  },

  lossTable: {
    width: "100%",
    borderCollapse: "collapse",
  },

  lossTable__th: {
    textAlign: "left",
    padding: "8px 12px",
    fontSize: "12px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#6C6C6C",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #EDEDED",
  },

  lossTable__thRight: {
    textAlign: "right",
  },

  lossTable__row: {
    "&:hover": {
      backgroundColor: "#F9F9F9",
    },
  },

  lossTable__td: {
    padding: "10px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    borderBottom: "1px solid #F0F0F0",
  },

  lossTable__tdRight: {
    textAlign: "right",
    fontWeight: 600,
  },

  noDataText: {
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    textAlign: "center",
    padding: "24px 0",
  },

  // ─── Sentences ───
  sentencesCard: {
    padding: "20px 24px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
  },

  sentencesCard__title: {
    margin: "0 0 16px 0",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#0C0E29",
  },

  sentencesList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  sentence: {
    margin: 0,
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    lineHeight: "1.6",
    padding: "8px 12px",
    borderRadius: "6px",
    backgroundColor: "#F9F9F9",
  },

  sentence__highlight: {
    fontWeight: 700,
    color: "#C3996B",
  },

  // ─── States ───
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },

  spinner: {
    width: "32px",
    height: "32px",
    border: "3px solid rgba(12, 14, 41, 0.1)",
    borderTop: "3px solid rgba(12, 14, 41, 0.7)",
    borderRadius: "50%",
    animation: "$spin 1s linear infinite",
  },

  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },

  emptyStateContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },

  emptyStateCard: {
    padding: "24px",
    textAlign: "center",
  },

  emptyStateCard__title: {
    margin: "0 0 8px 0",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#0C0E29",
  },

  emptyStateCard__subtitle: {
    margin: 0,
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
  },
}));
